/**
 * Reach Store — everything Nami has told this user out of band, as the app knows
 * it right now (A3-T2 Reach v1).
 *
 * Frames arrive two ways and the difference is load-bearing, so it rides on the
 * item rather than being inferred by whoever renders it:
 *   - `push`    — a LIVE nami:notify frame. Something just happened.
 *   - `hydrate` — a REPLAY of what was said while no browser was open. Old news,
 *                 and old news must never raise an OS-level banner.
 *
 * Seen and dismissed are different acts, and BOTH are durable — they just record
 * different things. `markSeen` (seen_at) is the only evidence anyone was reached.
 * `dismiss` (dismissed_at) is a refusal: it takes the row off every leg without
 * ever claiming it was read, so a dismissed notification stops chasing the user
 * while the unseen count stays honest.
 *
 * Both writes are fire-and-forget. The user's act is already done locally; making
 * them watch a round trip to close a toast would be the wrong trade.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { NamiNotifyPayload } from "@bash/shared";
import { markReachSeen, dismissReach } from "@/lib/api/reach";

export interface ReachItem extends NamiNotifyPayload {
  /** True when the frame arrived live over the socket; false when it was replayed. */
  live: boolean;
  /**
   * Whether the toaster has raised it. The STORE's to remember, not the toaster's:
   * the toaster lives in the (app) layout and remounts on every trip out and back
   * (/skills, the marketplace), while this store is the tab's. An instance-local
   * memory made each remount toast the whole store at once (09-26, five notices
   * from 09-21/22 in one burst). A replay is born announced: old news raises nothing.
   */
  announced: boolean;
}

/**
 * A ceiling, not a page size. Nothing here is ever refetched by id, so an
 * unbounded list would just grow for the life of the tab; the daily cap on the
 * rail is 20, so this holds well over a day of reach.
 */
const MAX_ITEMS = 50;

interface ReachStoreState {
  /** Newest first. Live arrivals sit above replayed ones. */
  items: ReachItem[];
}

interface ReachStoreActions {
  /** A live nami:notify frame, already parsed against the shared schema. */
  push: (payload: NamiNotifyPayload) => void;
  /** Replay what was said while nothing was listening. Never marks an item live. */
  hydrate: (payloads: NamiNotifyPayload[]) => void;
  /** The toaster raised it (or chose to stay silent about it): never again. */
  markAnnounced: (id: string) => void;
  /** The user opened it: stamp seen_at durably and take it off the surface. */
  markSeen: (id: string) => void;
  /** The user waved it away: stamp dismissed_at durably, claiming no sighting. */
  dismiss: (id: string) => void;
}

type ReachStore = ReachStoreState & ReachStoreActions;

export const useReachStore = create<ReachStore>()(
  devtools(
    (set, get) => ({
      items: [],

      push: (payload: NamiNotifyPayload) => {
        set((state) => {
          // The rail is at-least-once by design (a lost socket_emitted_at stamp makes
          // the sweeper redeliver), so a repeat id is expected traffic, not an error.
          if (state.items.some((i) => i.id === payload.id)) return state;
          return {
            items: [
              { ...payload, live: true, announced: false },
              ...state.items,
            ].slice(0, MAX_ITEMS),
          };
        });
      },

      hydrate: (payloads: NamiNotifyPayload[]) => {
        set((state) => {
          const known = new Set(state.items.map((i) => i.id));
          // A frame that already arrived live keeps that provenance: the replay
          // carries the same row, and downgrading it would be a lie about how the
          // user learned of it.
          const fresh = payloads
            .filter((p) => !known.has(p.id))
            .map((p) => ({ ...p, live: false, announced: true }));
          if (fresh.length === 0) return state;
          return { items: [...state.items, ...fresh].slice(0, MAX_ITEMS) };
        });
      },

      markAnnounced: (id: string) => {
        set((state) =>
          state.items.some((i) => i.id === id && !i.announced)
            ? {
                items: state.items.map((i) =>
                  i.id === id ? { ...i, announced: true } : i
                ),
              }
            : state
        );
      },

      markSeen: (id: string) => {
        set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
        void markReachSeen(id).catch((err) => {
          // Not re-raised: the click has already navigated, and throwing here would
          // break that. The cost is bounded and self-healing — the row still counts
          // as unseen, so the sweeper's email leg may mail something the user just
          // read, and the next replay brings it back. But silent is still wrong: a
          // stamp that never lands is the one failure that makes every reach number
          // in this system a lie, so it leaves a trace someone can find.
          console.debug("[reach] seen stamp did not land", id, err);
        });
      },

      dismiss: (id: string) => {
        // Read before writing rather than deciding inside the updater: an unknown id
        // is a no-op, not a durable write against a row this surface never held.
        if (!get().items.some((i) => i.id === id)) return;
        set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
        void dismissReach(id).catch((err) => {
          console.debug("[reach] dismissal did not land", id, err);
        });
      },
    }),
    { name: "ReachStore", enabled: process.env.NODE_ENV === "development" }
  )
);
